import type { PayloadAction } from '@reduxjs/toolkit';
import { createSlice } from '@reduxjs/toolkit';

import type { IExamFinishResponse } from '@/Module/Exam/Model/ApiModel';
import type { IExam, IExamQuestion, IExamResult, IExamUserExam } from '@/Module/Exam/Model/DataModel';
import type { UserExamStatusType } from '@/Module/Exam/Model/Enum';
import {
  answerExamQuestionAction,
  cancelExamAction,
  finishExamAction,
  getExamByContentIdAction,
  getExamQuestionListAction,
  getResultExamAction,
  startExamAction,
} from '@/Module/Exam/Store/Action';
import { ExamQuestionAnswerAdapter, QuestionAdapter } from '@/Module/Exam/Store/Adapter';

const ExamSlice = createSlice({
  name: 'exam',
  initialState: {
    isLoading: false,
    exam: null as IExam | null,
    answeredCount: 0,
    examResult: null as IExamResult | null,

    userExamId: null as IExamUserExam['id'] | null,
    userExamStatus: null as UserExamStatusType | null,

    selectedQuestionId: null as IExamQuestion['id'] | null,
    questions: QuestionAdapter.getInitialState({
      isLoading: false,
    }),
    questionAnswers: ExamQuestionAnswerAdapter.getInitialState(),
  },
  reducers: {
    reset: state => {
      state.exam = null;
    },
    setSelectedQuestionId: (state, { payload }: PayloadAction<IExamQuestion['id'] | null>) => {
      state.selectedQuestionId = payload;
    },
    incrementer: state => {
      state.answeredCount += 1;
    },
    decrementer: state => {
      state.answeredCount -= 1;
    },
  },
  extraReducers: builder => {
    builder
      .addCase(getExamByContentIdAction.pending, state => {
        state.isLoading = true;
      })
      .addCase(getExamByContentIdAction.rejected, state => {
        state.isLoading = false;
      })
      .addCase(getExamByContentIdAction.fulfilled, (state, { payload }) => {
        state.isLoading = false;
        state.exam = payload;
      });

    builder
      .addCase(getExamQuestionListAction.pending, state => {
        state.questions.isLoading = true;
      })
      .addCase(getExamQuestionListAction.rejected, state => {
        state.questions.isLoading = false;
      })
      .addCase(getExamQuestionListAction.fulfilled, (state, { payload }) => {
        state.questions.isLoading = false;
        QuestionAdapter.setAll(state.questions, payload);
      });

    builder
      .addCase(startExamAction.pending, state => {
        state.isLoading = true;
      })
      .addCase(startExamAction.rejected, state => {
        state.isLoading = false;
      })
      .addCase(startExamAction.fulfilled, (state, { payload }) => {
        state.isLoading = false;
        state.exam = payload.exam;
        state.userExamId = payload.userExamId;
        state.userExamStatus = payload.userExamStatus;
        QuestionAdapter.setAll(state.questions, payload.questions);
        state.selectedQuestionId = payload.questions?.[0]?.id ?? null;
      });

    builder
      .addCase(answerExamQuestionAction.pending, state => {
        state.questions.isLoading = true;
      })
      .addCase(answerExamQuestionAction.rejected, state => {
        state.questions.isLoading = false;
      })
      .addCase(answerExamQuestionAction.fulfilled, (state, { payload }) => {
        state.questions.isLoading = false;
        ExamQuestionAnswerAdapter.upsertOne(state.questionAnswers, payload);
      });

    builder
      .addCase(cancelExamAction.pending, state => {
        state.isLoading = true;
      })
      .addCase(cancelExamAction.rejected, state => {
        state.isLoading = false;
      })
      .addCase(cancelExamAction.fulfilled, state => {
        state.isLoading = false;
        state.examResult = null;
        state.userExamId = null;
        state.userExamStatus = null;
        state.selectedQuestionId = null;
        ExamQuestionAnswerAdapter.removeAll(state.questionAnswers);
      });

    builder
      .addMatcher(
        ({ type }) => [finishExamAction.pending.type, getResultExamAction.pending.type].includes(type),
        state => {
          state.isLoading = true;
        },
      )
      .addMatcher(
        ({ type }) => [finishExamAction.rejected.type, getResultExamAction.rejected.type].includes(type),
        state => {
          state.isLoading = false;
        },
      )
      .addMatcher(
        ({ type }) => [finishExamAction.fulfilled.type, getResultExamAction.fulfilled.type].includes(type),
        (state, { payload }: PayloadAction<IExamFinishResponse>) => {
          state.isLoading = false;
          state.exam = payload.exam;
          state.examResult = payload.examResult;
          state.userExamId = payload.userExamId;
          state.userExamStatus = payload.userExamStatus;
          QuestionAdapter.setAll(state.questions, payload.questions);
        },
      );
  },
});

export const {
  setSelectedQuestionId: examSetSelectedQuestionId,
  incrementer: addAnsweredQuestion,
  decrementer: decreaseAnsweredQuestion,
} = ExamSlice.actions;

export default ExamSlice;
